import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { FiltersGrid, Pagination } from '../types';
import { cachedItemByIdSelector, infiniteQueryOptions } from '../utils';
import { MeteringModem, MeteringModemFlat } from './types';

export const meteringModemApi = createApi({
  reducerPath: 'meteringModemApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/modems`,
  }),
  tagTypes: ['MeteringModem', 'MeteringModems'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getMeteringModemsI: builder.infiniteQuery<
      MeteringModemFlat,
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['MeteringModems', 'MeteringModem'],
    }),
    getMeteringModems: builder.query<MeteringModemFlat[], FiltersGrid>({
      query: params => ({
        url: '/query',
        method: 'GET',
        params,
      }),
      providesTags: () => ['MeteringModems', 'MeteringModem'],
    }),
    createMeteringModem: builder.mutation<MeteringModem, MeteringModem>({
      query: data => ({
        url: '/',
        method: 'POST',
        data: data,
      }),
      invalidatesTags: result => [
        'MeteringModem',
        { type: 'MeteringModem', id: result?.id },
      ],
    }),
    getMeteringModemById: builder.query<MeteringModem, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['MeteringModem', 'MeteringModems'],
    }),
    updateMeteringModemById: builder.mutation<MeteringModem, MeteringModem>({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data: data,
      }),
      invalidatesTags: result => [
        'MeteringModem',
        { type: 'MeteringModem', id: result?.id },
      ],
    }),
    deleteMeteringModemById: builder.mutation<MeteringModem, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'MeteringModem',
        { type: 'MeteringModem', id },
      ],
    }),
  }),
});

export const {
  useGetMeteringModemsQuery,
  useCreateMeteringModemMutation,
  useGetMeteringModemByIdQuery,
  useUpdateMeteringModemByIdMutation,
  useDeleteMeteringModemByIdMutation,
} = meteringModemApi;

export const useGetMeteringModemsInfiniteQuery = normalizeInfiniteQueryData(
  meteringModemApi.useGetMeteringModemsIInfiniteQuery,
);

const getCachedModemByIdSelector = cachedItemByIdSelector(
  'meteringModemApi',
  'getMeteringModems',
);

export const useGetCachedModemByIdSelector = id =>
  useSelector(getCachedModemByIdSelector(id));
